const QUANTITY_STEP = 1;
const PURCHASE_MODE_COLOR = 'color';

function resolveMinimumOrderQuantity(rawMinimumOrderQuantity) {
  // MOQ unset or invalid must behave as 1. This is a catalog rule, not a UI default.
  if (rawMinimumOrderQuantity === null || rawMinimumOrderQuantity === undefined || rawMinimumOrderQuantity === '') {
    return 1;
  }

  const parsedMinimumOrderQuantity = Number.parseInt(rawMinimumOrderQuantity, 10);

  if (!Number.isFinite(parsedMinimumOrderQuantity) || parsedMinimumOrderQuantity < 1) {
    return 1;
  }

  return parsedMinimumOrderQuantity;
}

function calculateSubtotal(unitPriceCents, quantity) {
  return unitPriceCents * quantity;
}

function calculateDiscountPerUnitCents(baseUnitPriceCents, discountTier) {
  if (!discountTier) {
    return 0;
  }

  // Merchant value 5 means 5% off each unit, not a 5x multiplier.
  return Math.round(baseUnitPriceCents * (discountTier.value / 100));
}

function calculateEffectiveUnitPrice(baseUnitPriceCents, discountTier) {
  return Math.max(0, baseUnitPriceCents - calculateDiscountPerUnitCents(baseUnitPriceCents, discountTier));
}

function calculateDiscountedSubtotal(effectiveUnitPriceCents, quantity) {
  return effectiveUnitPriceCents * quantity;
}

function calculateSavings(originalSubtotalCents, discountedSubtotalCents) {
  return originalSubtotalCents - discountedSubtotalCents;
}

function getEquidistantMarkerPosition(tierIndex, tierCount) {
  if (tierCount < 1) {
    return 0;
  }

  return ((tierIndex + 1) / tierCount) * 100;
}

function calculateSegmentedFillRatio(quantity, thresholds) {
  if (!thresholds.length) {
    return 0;
  }

  const lastThreshold = thresholds[thresholds.length - 1];
  if (quantity <= 0) {
    return 0;
  }

  if (quantity >= lastThreshold) {
    return 1;
  }

  const segmentCount = thresholds.length;
  let previousThreshold = 0;

  for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex += 1) {
    const segmentThreshold = thresholds[segmentIndex];

    if (quantity <= segmentThreshold) {
      const segmentSpan = segmentThreshold - previousThreshold;
      const segmentProgress = segmentSpan > 0
        ? (quantity - previousThreshold) / segmentSpan
        : 1;
      return (segmentIndex + segmentProgress) / segmentCount;
    }

    previousThreshold = segmentThreshold;
  }

  return 1;
}

function buildVolumeDiscountState(rawVolumeDiscounts, addQuantity, cartQuantity, effectiveQuantity, baseUnitPriceCents) {
  const normalizedTiers = normalizeVolumeDiscounts(rawVolumeDiscounts);
  const currentActiveTier = findActiveDiscountTier(normalizedTiers, cartQuantity);
  const projectedActiveTier = findActiveDiscountTier(normalizedTiers, effectiveQuantity);
  const nextUnlockTier = findNextDiscountTier(normalizedTiers, cartQuantity);
  const lastThreshold = normalizedTiers.length
    ? normalizedTiers[normalizedTiers.length - 1].threshold
    : 0;
  const thresholds = normalizedTiers.map((discountTier) => discountTier.threshold);
  const originalSubtotal = calculateSubtotal(baseUnitPriceCents, addQuantity);
  const effectiveUnitPrice = calculateEffectiveUnitPrice(baseUnitPriceCents, projectedActiveTier);
  const discountedSubtotal = calculateDiscountedSubtotal(effectiveUnitPrice, addQuantity);

  return {
    normalizedTiers: normalizedTiers,
    currentActiveTier: currentActiveTier,
    projectedActiveTier: projectedActiveTier,
    nextUnlockTier: nextUnlockTier,
    lastThreshold: lastThreshold,
    unitsToNextUnlock: nextUnlockTier ? nextUnlockTier.threshold - cartQuantity : null,
    originalSubtotal: originalSubtotal,
    effectiveUnitPrice: effectiveUnitPrice,
    discountedSubtotal: discountedSubtotal,
    savings: calculateSavings(originalSubtotal, discountedSubtotal),
    currentBarFillRatio: calculateSegmentedFillRatio(cartQuantity, thresholds),
    projectedBarFillRatio: calculateSegmentedFillRatio(effectiveQuantity, thresholds)
  };
}

function interpolateTemplate(template, replacements) {
  let interpolatedText = template || '';

  Object.keys(replacements).forEach((placeholder) => {
    interpolatedText = interpolatedText.split(placeholder).join(String(replacements[placeholder]));
  });

  return interpolatedText;
}

function formatVolumeDiscountOffer(discountTier, copy) {
  return interpolateTemplate(copy.percentageOffTemplate, {
    __PERCENTAGE__: discountTier.value
  });
}

function resolveCartQuantity(rawCartQuantity) {
  const parsedCartQuantity = Number.parseInt(rawCartQuantity, 10);

  if (!Number.isFinite(parsedCartQuantity) || parsedCartQuantity < 0) {
    return 0;
  }

  return parsedCartQuantity;
}

function resolveEffectiveQuantity(cartQuantity, addQuantity) {
  return cartQuantity + addQuantity;
}

function sumCartQuantityForVariant(cartItems, variantId) {
  if (!Array.isArray(cartItems)) {
    return 0;
  }

  let totalQuantity = 0;

  cartItems.forEach((cartItem) => {
    if (Number(cartItem.variant_id) !== Number(variantId)) {
      return;
    }

    const lineQuantity = Number.parseInt(cartItem.quantity, 10);
    if (Number.isFinite(lineQuantity) && lineQuantity > 0) {
      totalQuantity += lineQuantity;
    }
  });

  return totalQuantity;
}

function clampQuantityToMinimum(quantity, minimumAddQuantity) {
  return Math.max(quantity, minimumAddQuantity);
}

function resolveColorQuantity(rawColorQuantity) {
  const parsedColorQuantity = Number.parseInt(rawColorQuantity, 10);

  if (!Number.isFinite(parsedColorQuantity) || parsedColorQuantity < 0) {
    return 0;
  }

  return parsedColorQuantity;
}

function sumSelectedColorQuantities(colorQuantities) {
  let selectedQuantity = 0;

  colorQuantities.forEach((colorQuantity) => {
    selectedQuantity += colorQuantity;
  });

  return selectedQuantity;
}

function sumVariantCartQuantities(variants) {
  return variants.reduce((totalCartQuantity, variant) => {
    return totalCartQuantity + resolveCartQuantity(variant.cartQuantity);
  }, 0);
}

function buildPurchaseQuantities(minimumOrderQuantity, cartQuantity, addQuantity) {
  return {
    cartQuantity: cartQuantity,
    addQuantity: addQuantity,
    minimumAddQuantity: resolveMinimumAddQuantity(minimumOrderQuantity, cartQuantity),
    effectiveQuantity: resolveEffectiveQuantity(cartQuantity, addQuantity)
  };
}

function hasReachedMinimumAddQuantity(purchaseQuantities) {
  return purchaseQuantities.addQuantity >= purchaseQuantities.minimumAddQuantity;
}

function shouldExplainMinimumOrderQuantity(minimumOrderQuantity, addQuantity, minimumAddQuantity) {
  return minimumOrderQuantity > 1
    && minimumAddQuantity > 1
    && addQuantity <= minimumAddQuantity;
}

function findVariantBySelectedOptions(variants, selectedOptionValues) {
  // Only used for gift cards. Standard PDPs have no option UI
  // and a payload that contains variants.first only.
  return variants.find((variant) => {
    return selectedOptionValues.every((optionValue, optionIndex) => {
      return variant.options[optionIndex] === optionValue;
    });
  }) || null;
}

function parseJsonResponse(responseText) {
  try {
    return JSON.parse(responseText);
  } catch (parseError) {
    return null;
  }
}

function getCartAddUrl() {
  if (window.theme && theme.routes && theme.routes.cartAdd) {
    return theme.routes.cartAdd;
  }

  const shopifyRoot = window.Shopify && Shopify.routes && Shopify.routes.root
    ? Shopify.routes.root
    : '/';

  return shopifyRoot + 'cart/add.js';
}

function formatMoney(cents, moneyFormat) {
  if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
    return theme.Currency.formatMoney(cents, moneyFormat, true);
  }

  const amount = (Number(cents) / 100).toFixed(2);
  if (!moneyFormat) {
    return '$' + amount;
  }

  return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
}

function readSelectedOptionValues(optionGroups) {
  return optionGroups.map((optionGroup) => {
    const checkedInput = optionGroup.querySelector('.js-pdp-cfy-option-input:checked');
    return checkedInput ? checkedInput.value : null;
  });
}

class ProductGallery {
  constructor(rootElement) {
    this.rootElement = rootElement;
    this.viewport = rootElement.querySelector('.pdp-cfy-gallery__viewport');
    this.slides = Array.from(rootElement.querySelectorAll('.js-pdp-cfy-slide'));
    this.thumbs = Array.from(rootElement.querySelectorAll('.js-pdp-cfy-thumb'));
    this.previousButton = rootElement.querySelector('.js-pdp-cfy-previous-media');
    this.nextButton = rootElement.querySelector('.js-pdp-cfy-next-media');
    this.isProgrammaticScroll = false;
    this.programmaticScrollTimeoutId = null;

    this.bindEvents();
    this.syncViewportToActiveSlide(false);
  }

  bindEvents() {
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.showMedia(thumb.dataset.mediaId);
      });
    });

    if (this.previousButton) {
      this.previousButton.addEventListener('click', () => this.stepMedia(-1));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.stepMedia(1));
    }

    if (this.viewport) {
      this.viewport.addEventListener('scroll', () => this.handleViewportScroll());
    }
  }

  getActiveIndex() {
    return this.slides.findIndex((slide) => slide.classList.contains('js-pdp-cfy-active-slide'));
  }

  stepMedia(direction) {
    if (this.slides.length === 0) {
      return;
    }

    const currentIndex = this.getActiveIndex();
    const nextIndex = (currentIndex + direction + this.slides.length) % this.slides.length;
    this.showMedia(this.slides[nextIndex].dataset.mediaId);
  }

  handleViewportScroll() {
    if (this.isProgrammaticScroll || !this.viewport || this.slides.length === 0) {
      return;
    }

    const viewportCenter = this.viewport.scrollLeft + (this.viewport.clientWidth / 2);
    let closestSlide = this.slides[0];
    let closestDistance = Infinity;

    this.slides.forEach((slide) => {
      const slideCenter = slide.offsetLeft + (slide.offsetWidth / 2);
      const distance = Math.abs(slideCenter - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlide = slide;
      }
    });

    this.setActiveMedia(closestSlide.dataset.mediaId, false);
  }

  setActiveMedia(mediaId, shouldScroll) {
    if (!mediaId) {
      return;
    }

    this.slides.forEach((slide) => {
      const isActive = slide.dataset.mediaId === String(mediaId);
      slide.classList.toggle('pdp-cfy-gallery__slide--active', isActive);
      slide.classList.toggle('js-pdp-cfy-active-slide', isActive);
    });

    this.thumbs.forEach((thumb) => {
      const isActive = thumb.dataset.mediaId === String(mediaId);
      thumb.classList.toggle('pdp-cfy-gallery__thumb--active', isActive);
      thumb.classList.toggle('js-pdp-cfy-active-thumb', isActive);
      thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (shouldScroll) {
      this.syncViewportToActiveSlide(true);
    }
  }

  syncViewportToActiveSlide(smooth) {
    if (!this.viewport) {
      return;
    }

    const activeSlide = this.rootElement.querySelector('.js-pdp-cfy-active-slide');
    if (!activeSlide) {
      return;
    }

    this.isProgrammaticScroll = true;
    this.viewport.scrollTo({
      left: activeSlide.offsetLeft,
      behavior: smooth ? 'smooth' : 'auto'
    });

    if (this.programmaticScrollTimeoutId) {
      window.clearTimeout(this.programmaticScrollTimeoutId);
    }

    this.programmaticScrollTimeoutId = window.setTimeout(() => {
      this.isProgrammaticScroll = false;
    }, 400);
  }

  showMedia(mediaId) {
    this.setActiveMedia(mediaId, true);
  }

  showVariantMedia(featuredMediaId) {
    if (!featuredMediaId) {
      return;
    }

    const matchingSlide = this.slides.find((slide) => slide.dataset.mediaId === String(featuredMediaId));
    if (!matchingSlide) {
      return;
    }

    this.showMedia(featuredMediaId);
  }
}

class ProductPurchaseController {
  constructor(rootElement) {
    this.rootElement = rootElement;
    this.variants = this.readVariantPayload();
    this.moneyFormat = rootElement.dataset.moneyFormat;
    this.addToCartLabel = rootElement.dataset.addToCartLabel;
    this.soldOutLabel = rootElement.dataset.soldOutLabel;
    this.unavailableLabel = rootElement.dataset.unavailableLabel;
    this.inStockLabel = rootElement.dataset.inStockLabel;
    this.minimumOrderQuantity = resolveMinimumOrderQuantity(rootElement.dataset.minimumOrderQuantity);
    this.isColorPurchase = rootElement.dataset.purchaseMode === PURCHASE_MODE_COLOR;
    this.rawVolumeDiscounts = rootElement.dataset.volumeDiscounts;

    this.variantIdInput = rootElement.querySelector('.js-pdp-cfy-variant-id');
    this.quantityInput = rootElement.querySelector('.js-pdp-cfy-quantity-input');
    this.decreaseButton = rootElement.querySelector('.js-pdp-cfy-quantity-decrease');
    this.increaseButton = rootElement.querySelector('.js-pdp-cfy-quantity-increase');
    this.quantityRoot = rootElement.querySelector('.js-pdp-cfy-quantity');
    this.moqInfoElement = rootElement.querySelector('.js-pdp-cfy-moq-info');
    this.volumeBarCurrent = rootElement.querySelector('.js-pdp-cfy-volume-bar-current');
    this.volumeBarProjected = rootElement.querySelector('.js-pdp-cfy-volume-bar-projected');
    this.volumeTicks = rootElement.querySelector('.js-pdp-cfy-volume-ticks');
    this.volumeMarkers = rootElement.querySelector('.js-pdp-cfy-volume-markers');
    this.unitPriceElement = rootElement.querySelector('.js-pdp-cfy-unit-price');
    this.originalPriceElement = rootElement.querySelector('.js-pdp-cfy-unit-price-original');
    this.priceElement = rootElement.querySelector('.js-pdp-cfy-price');
    this.volumeRoot = rootElement.querySelector('.js-pdp-cfy-volume');
    this.volumeProgress = rootElement.querySelector('.js-pdp-cfy-volume-progress');
    this.volumeIntro = rootElement.querySelector('.js-pdp-cfy-volume-intro');
    this.volumeStatus = rootElement.querySelector('.js-pdp-cfy-volume-status');
    this.volumeBadge = rootElement.querySelector('.js-pdp-cfy-volume-badge');
    this.volumeBadgeCheck = rootElement.querySelector('.js-pdp-cfy-volume-badge-check');
    this.volumeBadgeText = rootElement.querySelector('.js-pdp-cfy-volume-badge-text');
    this.volumeSegmentTemplate = rootElement.querySelector('.js-pdp-cfy-volume-marker-template');
    this.volumeCopy = this.readVolumeCopy();
    this.skuElement = rootElement.querySelector('.js-pdp-cfy-sku');
    this.skuValueElement = rootElement.querySelector('.js-pdp-cfy-sku-value');
    this.catalogMoqElement = rootElement.querySelector('.js-pdp-cfy-catalog-moq');
    this.availabilityLabel = rootElement.querySelector('.js-pdp-cfy-availability-label');
    this.addToCartButton = rootElement.querySelector('.js-pdp-cfy-add-to-cart');
    this.addToCartLabelElement = rootElement.querySelector('.js-pdp-cfy-add-to-cart-label');
    this.optionGroups = Array.from(rootElement.querySelectorAll('.js-pdp-cfy-option-group'));
    this.form = rootElement.querySelector('.js-pdp-cfy-form');
    this.summaryTrigger = rootElement.querySelector('.js-pdp-cfy-summary-trigger');
    this.summaryContent = rootElement.querySelector('.js-pdp-cfy-summary-content');
    this.colorSelectorRoot = rootElement.querySelector('.js-pdp-cfy-color-selector');
    this.colorProgressElement = rootElement.querySelector('.js-pdp-cfy-color-progress');
    this.colorRows = Array.from(rootElement.querySelectorAll('.js-pdp-cfy-color-row')).map((colorRowElement) => {
      return this.readColorRow(colorRowElement);
    });
    this.colorVariants = this.colorRows.map((colorRow) => colorRow.variant);

    const galleryRoot = rootElement.querySelector('.js-pdp-cfy-gallery');
    this.gallery = galleryRoot ? new ProductGallery(galleryRoot) : null;

    const initialVariantId = this.variantIdInput ? Number.parseInt(this.variantIdInput.value, 10) : this.variants[0].id;
    const initialVariant = this.getVariantById(initialVariantId) || this.variants[0];
    const initialCartQuantity = resolveCartQuantity(initialVariant.cartQuantity);
    const initialMinimumAddQuantity = resolveMinimumAddQuantity(
      this.minimumOrderQuantity,
      initialCartQuantity
    );
    const initialAddQuantity = this.quantityInput
      ? Number.parseInt(this.quantityInput.value, 10)
      : initialMinimumAddQuantity;

    // In color mode selectedVariant is the first color: all colors share its price,
    // while colorQuantities holds what the shopper picked per color.
    this.state = {
      selectedVariant: initialVariant,
      addQuantity: clampQuantityToMinimum(initialAddQuantity, initialMinimumAddQuantity),
      colorQuantities: new Map(this.colorVariants.map((colorVariant) => [colorVariant.id, 0]))
    };

    this.isSubmitting = false;
    this.moqInfoTimeoutId = null;
    this.boundCartUpdatedHandler = (event) => this.handleCartUpdated(event);
    this.boundHideMoqInfoHandler = () => this.hideMinimumOrderQuantityInfo();
    this.bindEvents();
    this.render();
  }

  readVariantPayload() {
    const payloadElement = this.rootElement.querySelector('.js-pdp-cfy-variant-payload');
    if (!payloadElement) {
      throw new Error('CFY PDP is missing the variant payload.');
    }

    return JSON.parse(payloadElement.textContent);
  }

  getVariantById(variantId) {
    return this.variants.find((variant) => variant.id === Number(variantId)) || null;
  }

  readColorRow(colorRowElement) {
    const colorVariant = this.getVariantById(colorRowElement.dataset.variantId);
    if (!colorVariant) {
      throw new Error(`CFY PDP color row references variant ${colorRowElement.dataset.variantId}, which is missing from the payload.`);
    }

    return {
      variant: colorVariant,
      swatchButton: colorRowElement.querySelector('.js-pdp-cfy-color-swatch'),
      decreaseButton: colorRowElement.querySelector('.js-pdp-cfy-color-decrease'),
      increaseButton: colorRowElement.querySelector('.js-pdp-cfy-color-increase'),
      quantityInput: colorRowElement.querySelector('.js-pdp-cfy-color-input')
    };
  }

  readVolumeCopy() {
    if (!this.volumeRoot) {
      return null;
    }

    return {
      youSavePercentageTemplate: this.volumeRoot.dataset.youSavePercentageTemplate,
      addMoreOneTemplate: this.volumeRoot.dataset.addMoreOneTemplate,
      addMoreOtherTemplate: this.volumeRoot.dataset.addMoreOtherTemplate,
      highestTierLabel: this.volumeRoot.dataset.highestTierLabel,
      cartOneTemplate: this.volumeRoot.dataset.cartOneTemplate,
      cartOtherTemplate: this.volumeRoot.dataset.cartOtherTemplate,
      percentageOffTemplate: this.volumeRoot.dataset.percentageOffTemplate
    };
  }

  bindEvents() {
    this.optionGroups.forEach((optionGroup) => {
      optionGroup.addEventListener('change', () => this.handleOptionChange());
    });

    if (this.decreaseButton) {
      this.decreaseButton.addEventListener('click', (event) => {
        event.stopPropagation();
        this.handleDecreaseQuantity();
      });
    }

    if (this.increaseButton) {
      this.increaseButton.addEventListener('click', () => {
        this.hideMinimumOrderQuantityInfo();
        this.adjustQuantity(QUANTITY_STEP);
      });
    }

    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', () => this.handleQuantityInputChange());
    }

    this.colorRows.forEach((colorRow) => {
      colorRow.swatchButton.addEventListener('click', () => {
        this.previewColorVariant(colorRow.variant);
      });

      colorRow.decreaseButton.addEventListener('click', () => {
        this.adjustColorQuantity(colorRow.variant, -QUANTITY_STEP);
      });

      colorRow.increaseButton.addEventListener('click', () => {
        this.adjustColorQuantity(colorRow.variant, QUANTITY_STEP);
      });

      colorRow.quantityInput.addEventListener('change', () => {
        this.setColorQuantity(colorRow.variant, colorRow.quantityInput.value);
      });
    });

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.handleAddToCart(event));
    }

    if (this.addToCartButton && this.form) {
      this.addToCartButton.addEventListener('click', (event) => {
        if (this.addToCartButton.getAttribute('form')) {
          event.preventDefault();
          this.handleAddToCart(event);
        }
      });
    }

    if (this.summaryTrigger && this.summaryContent) {
      this.summaryTrigger.addEventListener('click', () => this.toggleSummary());
    }

    document.addEventListener('cart:updated', this.boundCartUpdatedHandler);
    document.addEventListener('click', this.boundHideMoqInfoHandler);
  }

  handleOptionChange() {
    const selectedOptionValues = readSelectedOptionValues(this.optionGroups);
    const matchedVariant = findVariantBySelectedOptions(this.variants, selectedOptionValues);
    this.setSelectedVariant(matchedVariant);
  }

  setSelectedVariant(variant) {
    const previousAddQuantity = this.state.addQuantity;
    this.state.selectedVariant = variant;

    if (variant) {
      // Switching variants never lowers addQuantity below the new variant's remaining MOQ.
      this.state.addQuantity = clampQuantityToMinimum(
        previousAddQuantity,
        this.getMinimumAddQuantity(variant)
      );
    }

    this.render();
  }

  getMinimumAddQuantity(variant) {
    const cartQuantity = resolveCartQuantity(variant.cartQuantity);
    return resolveMinimumAddQuantity(this.minimumOrderQuantity, cartQuantity);
  }

  getPurchaseQuantities() {
    if (this.isColorPurchase) {
      // Any mix of colors counts toward the MOQ, including colors already in the cart.
      return buildPurchaseQuantities(
        this.minimumOrderQuantity,
        sumVariantCartQuantities(this.colorVariants),
        sumSelectedColorQuantities(this.state.colorQuantities)
      );
    }

    return buildPurchaseQuantities(
      this.minimumOrderQuantity,
      resolveCartQuantity(this.state.selectedVariant.cartQuantity),
      this.state.addQuantity
    );
  }

  previewColorVariant(colorVariant) {
    if (!this.gallery) {
      return;
    }

    this.gallery.showVariantMedia(colorVariant.featuredMediaId);
  }

  adjustColorQuantity(colorVariant, step) {
    const currentColorQuantity = this.state.colorQuantities.get(colorVariant.id);
    this.setColorQuantity(colorVariant, currentColorQuantity + step);
  }

  setColorQuantity(colorVariant, rawColorQuantity) {
    if (!colorVariant.available) {
      return;
    }

    const nextColorQuantity = resolveColorQuantity(rawColorQuantity);
    this.state.colorQuantities.set(colorVariant.id, nextColorQuantity);

    if (nextColorQuantity > 0) {
      this.previewColorVariant(colorVariant);
    }

    this.render();
  }

  resetColorQuantities() {
    this.colorVariants.forEach((colorVariant) => {
      this.state.colorQuantities.set(colorVariant.id, 0);
    });
  }

  adjustQuantity(step) {
    const nextAddQuantity = this.state.addQuantity + step;
    this.setAddQuantity(nextAddQuantity);
  }

  handleDecreaseQuantity() {
    if (!this.state.selectedVariant) {
      return;
    }

    const minimumAddQuantity = this.getMinimumAddQuantity(this.state.selectedVariant);

    if (this.state.addQuantity <= minimumAddQuantity) {
      if (shouldExplainMinimumOrderQuantity(
        this.minimumOrderQuantity,
        this.state.addQuantity,
        minimumAddQuantity
      )) {
        this.showMinimumOrderQuantityInfo();
      }
      return;
    }

    this.hideMinimumOrderQuantityInfo();
    this.adjustQuantity(-QUANTITY_STEP);
  }

  showMinimumOrderQuantityInfo() {
    if (!this.moqInfoElement || !this.quantityRoot) {
      return;
    }

    this.moqInfoElement.hidden = false;
    this.moqInfoElement.textContent = interpolateTemplate(
      this.quantityRoot.dataset.minimumOrderQuantityTemplate,
      { __COUNT__: this.minimumOrderQuantity }
    );

    if (this.moqInfoTimeoutId) {
      window.clearTimeout(this.moqInfoTimeoutId);
    }

    this.moqInfoTimeoutId = window.setTimeout(() => {
      this.hideMinimumOrderQuantityInfo();
    }, 4000);
  }

  hideMinimumOrderQuantityInfo() {
    if (this.moqInfoTimeoutId) {
      window.clearTimeout(this.moqInfoTimeoutId);
      this.moqInfoTimeoutId = null;
    }

    if (this.moqInfoElement) {
      this.moqInfoElement.hidden = true;
    }
  }

  handleQuantityInputChange() {
    const parsedAddQuantity = Number.parseInt(this.quantityInput.value, 10);
    this.setAddQuantity(parsedAddQuantity);
  }

  setAddQuantity(addQuantity) {
    if (!this.state.selectedVariant) {
      return;
    }

    const minimumAddQuantity = this.getMinimumAddQuantity(this.state.selectedVariant);

    if (!Number.isFinite(addQuantity)) {
      this.state.addQuantity = minimumAddQuantity;
    } else {
      this.state.addQuantity = clampQuantityToMinimum(addQuantity, minimumAddQuantity);
    }

    this.render();
  }

  handleCartUpdated(event) {
    const cart = event.detail && event.detail.cart;
    if (!cart || !Array.isArray(cart.items)) {
      return;
    }

    this.variants.forEach((variant) => {
      variant.cartQuantity = sumCartQuantityForVariant(cart.items, variant.id);
    });

    if (!this.state.selectedVariant || this.isColorPurchase) {
      this.render();
      return;
    }

    this.setAddQuantity(this.state.addQuantity);
  }

  toggleSummary() {
    const isOpen = this.summaryContent.classList.toggle('pdp-cfy-summary__content--open');
    this.summaryTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  render() {
    const selectedVariant = this.state.selectedVariant;

    if (!selectedVariant) {
      this.renderUnavailable();
      return;
    }

    const purchaseQuantities = this.getPurchaseQuantities();
    const volumeDiscountState = buildVolumeDiscountState(
      this.rawVolumeDiscounts,
      purchaseQuantities.addQuantity,
      purchaseQuantities.cartQuantity,
      purchaseQuantities.effectiveQuantity,
      selectedVariant.price
    );

    this.variantIdInput.value = String(selectedVariant.id);

    if (this.isColorPurchase) {
      this.renderColorQuantities(purchaseQuantities);
    } else {
      this.renderSingleQuantity(purchaseQuantities);
    }

    this.renderUnitPrice(selectedVariant.price, volumeDiscountState.effectiveUnitPrice);
    this.renderVolumeDiscounts(volumeDiscountState, purchaseQuantities.cartQuantity);

    if (this.skuValueElement) {
      this.skuValueElement.textContent = selectedVariant.sku || '';
    }

    if (this.skuElement) {
      this.skuElement.classList.toggle('pdp-cfy__sku--empty', !selectedVariant.sku);
    }

    if (this.catalogMoqElement) {
      this.catalogMoqElement.textContent = interpolateTemplate(
        this.catalogMoqElement.dataset.template,
        { __COUNT__: this.minimumOrderQuantity }
      );
    }

    const isPurchasable = this.hasAvailablePurchaseVariant();
    this.availabilityLabel.textContent = isPurchasable ? this.inStockLabel : this.soldOutLabel;
    this.addToCartButton.disabled = !this.canAddToCart(purchaseQuantities);
    this.addToCartLabelElement.textContent = isPurchasable ? this.addToCartLabel : this.soldOutLabel;

    // Color mode has no single selected variant: the gallery follows the last
    // increased color and the URL is left untouched.
    if (this.isColorPurchase) {
      return;
    }

    if (this.gallery) {
      this.gallery.showVariantMedia(selectedVariant.featuredMediaId);
    }

    this.updateHistory(selectedVariant.id);
  }

  renderSingleQuantity(purchaseQuantities) {
    const addQuantity = purchaseQuantities.addQuantity;
    const minimumAddQuantity = purchaseQuantities.minimumAddQuantity;

    this.quantityInput.value = String(addQuantity);
    this.quantityInput.min = String(minimumAddQuantity);
    const isAtMinimumAddQuantity = addQuantity <= minimumAddQuantity;
    if (this.decreaseButton) {
      this.decreaseButton.setAttribute('aria-disabled', isAtMinimumAddQuantity ? 'true' : 'false');
      this.decreaseButton.classList.toggle('pdp-cfy-quantity__button--disabled', isAtMinimumAddQuantity);
    }

    if (!shouldExplainMinimumOrderQuantity(this.minimumOrderQuantity, addQuantity, minimumAddQuantity)) {
      this.hideMinimumOrderQuantityInfo();
    }
  }

  renderColorQuantities(purchaseQuantities) {
    this.colorRows.forEach((colorRow) => {
      const colorQuantity = this.state.colorQuantities.get(colorRow.variant.id);
      const isColorAvailable = colorRow.variant.available;

      colorRow.quantityInput.value = String(colorQuantity);
      colorRow.quantityInput.disabled = !isColorAvailable;
      colorRow.decreaseButton.disabled = !isColorAvailable || colorQuantity <= 0;
      colorRow.increaseButton.disabled = !isColorAvailable;
    });

    this.renderColorMinimumProgress(purchaseQuantities);
  }

  renderColorMinimumProgress(purchaseQuantities) {
    if (!this.colorProgressElement || !this.colorSelectorRoot) {
      return;
    }

    // A remaining minimum of 1 means there is no MOQ left to explain (unset, or already met by the cart).
    const hasMinimumToReach = purchaseQuantities.minimumAddQuantity > 1;
    this.colorProgressElement.hidden = !hasMinimumToReach;

    if (!hasMinimumToReach) {
      return;
    }

    this.colorProgressElement.textContent = interpolateTemplate(
      this.colorSelectorRoot.dataset.minimumProgressTemplate,
      {
        __SELECTED__: purchaseQuantities.addQuantity,
        __MINIMUM__: purchaseQuantities.minimumAddQuantity
      }
    );
    this.colorProgressElement.classList.toggle(
      'pdp-cfy-colors__progress--reached',
      hasReachedMinimumAddQuantity(purchaseQuantities)
    );
  }

  hasAvailablePurchaseVariant() {
    if (this.isColorPurchase) {
      return this.colorVariants.some((colorVariant) => colorVariant.available);
    }

    return this.state.selectedVariant.available;
  }

  canAddToCart(purchaseQuantities) {
    if (!this.state.selectedVariant || !this.hasAvailablePurchaseVariant()) {
      return false;
    }

    if (this.isColorPurchase) {
      return hasReachedMinimumAddQuantity(purchaseQuantities);
    }

    return true;
  }

  buildCartLineItems() {
    if (!this.isColorPurchase) {
      return [{
        id: this.state.selectedVariant.id,
        quantity: this.state.addQuantity
      }];
    }

    const cartLineItems = [];

    this.colorVariants.forEach((colorVariant) => {
      const colorQuantity = this.state.colorQuantities.get(colorVariant.id);
      if (!colorVariant.available || colorQuantity <= 0) {
        return;
      }

      cartLineItems.push({
        id: colorVariant.id,
        quantity: colorQuantity
      });
    });

    return cartLineItems;
  }

  renderUnavailable() {
    this.addToCartButton.disabled = true;
    this.addToCartLabelElement.textContent = this.unavailableLabel;
    this.availabilityLabel.textContent = this.unavailableLabel;

    if (this.volumeRoot) {
      this.volumeRoot.hidden = true;
    }
  }

  renderUnitPrice(baseUnitPriceCents, effectiveUnitPriceCents) {
    if (!this.unitPriceElement) {
      return;
    }

    const hasDiscount = effectiveUnitPriceCents < baseUnitPriceCents;

    this.unitPriceElement.innerHTML = formatMoney(
      hasDiscount ? effectiveUnitPriceCents : baseUnitPriceCents,
      this.moneyFormat
    );

    if (this.originalPriceElement) {
      this.originalPriceElement.hidden = !hasDiscount;
      this.originalPriceElement.innerHTML = hasDiscount
        ? formatMoney(baseUnitPriceCents, this.moneyFormat)
        : '';
    }

    if (this.priceElement) {
      this.priceElement.classList.toggle('pdp-cfy__price--discounted', hasDiscount);
    }
  }

  renderVolumeDiscounts(volumeDiscountState, cartQuantity) {
    if (!this.volumeRoot || !this.volumeCopy) {
      return;
    }

    if (volumeDiscountState.normalizedTiers.length === 0) {
      this.volumeRoot.hidden = true;
      return;
    }

    this.volumeRoot.hidden = false;
    this.volumeRoot.classList.toggle(
      'pdp-cfy-volume--four-tiers',
      volumeDiscountState.normalizedTiers.length === 4
    );
    this.renderVolumeIntro(volumeDiscountState, cartQuantity);
    this.renderVolumeProgress(volumeDiscountState);
    this.renderVolumeBadge(volumeDiscountState);
  }

  renderVolumeProgress(volumeDiscountState) {
    if (
      !this.volumeProgress ||
      !this.volumeBarCurrent ||
      !this.volumeBarProjected ||
      !this.volumeTicks ||
      !this.volumeMarkers ||
      !this.volumeSegmentTemplate
    ) {
      return;
    }

    const lastThreshold = volumeDiscountState.lastThreshold;
    const currentFillRatio = volumeDiscountState.currentBarFillRatio;
    const projectedFillRatio = volumeDiscountState.projectedBarFillRatio;
    const projectedDelta = Math.max(0, projectedFillRatio - currentFillRatio);

    this.volumeBarCurrent.style.width = (currentFillRatio * 100) + '%';
    this.volumeBarProjected.style.left = (currentFillRatio * 100) + '%';
    this.volumeBarProjected.style.width = (projectedDelta * 100) + '%';
    this.volumeTicks.replaceChildren();
    this.volumeMarkers.replaceChildren();

    const tierCount = volumeDiscountState.normalizedTiers.length;
    this.volumeMarkers.style.setProperty('--pdp-cfy-volume-tier-count', String(tierCount));

    volumeDiscountState.normalizedTiers.forEach((discountTier, tierIndex) => {
      const markerPosition = getEquidistantMarkerPosition(tierIndex, tierCount);
      const isLastMarker = tierIndex === tierCount - 1;
      const tickElement = document.createElement('span');
      tickElement.className = 'pdp-cfy-volume__tick';

      if (isLastMarker) {
        tickElement.style.left = 'auto';
        tickElement.style.right = '0';
        tickElement.style.transform = 'none';
      } else {
        tickElement.style.left = markerPosition + '%';
      }

      this.volumeTicks.appendChild(tickElement);

      const markerElement = this.volumeSegmentTemplate.content.cloneNode(true);
      const offerElement = markerElement.querySelector('.js-pdp-cfy-volume-segment-offer');
      offerElement.textContent = formatVolumeDiscountOffer(discountTier, this.volumeCopy);
      this.volumeMarkers.appendChild(markerElement);
    });
  }

  renderVolumeBadge(volumeDiscountState) {
    if (!this.volumeBadge || !this.volumeBadgeText) {
      return;
    }

    const currentActiveTier = volumeDiscountState.currentActiveTier;
    this.volumeBadge.hidden = !currentActiveTier;
    this.volumeBadge.classList.toggle('pdp-cfy-volume__badge--active', Boolean(currentActiveTier));

    if (this.volumeStatus) {
      this.volumeStatus.hidden = !currentActiveTier;
    }

    if (this.volumeBadgeCheck) {
      this.volumeBadgeCheck.hidden = !currentActiveTier;
    }

    if (!currentActiveTier) {
      this.volumeBadgeText.textContent = '';
      return;
    }

    this.volumeBadgeText.textContent = interpolateTemplate(this.volumeCopy.youSavePercentageTemplate, {
      __PERCENTAGE__: currentActiveTier.value
    });
  }

  renderVolumeIntro(volumeDiscountState, cartQuantity) {
    if (!this.volumeIntro) {
      return;
    }

    const introParts = [];

    if (volumeDiscountState.nextUnlockTier) {
      const offerLabel = formatVolumeDiscountOffer(
        volumeDiscountState.nextUnlockTier,
        this.moneyFormat,
        this.volumeCopy
      );

      const addMoreTemplate = volumeDiscountState.unitsToNextUnlock === 1
        ? this.volumeCopy.addMoreOneTemplate
        : this.volumeCopy.addMoreOtherTemplate;

      introParts.push(interpolateTemplate(addMoreTemplate, {
        __COUNT__: volumeDiscountState.unitsToNextUnlock,
        __OFFER__: offerLabel
      }));
    } else if (volumeDiscountState.currentActiveTier) {
      introParts.push(this.volumeCopy.highestTierLabel);
    }

    if (cartQuantity > 0) {
      const cartTemplate = cartQuantity === 1
        ? this.volumeCopy.cartOneTemplate
        : this.volumeCopy.cartOtherTemplate;

      introParts.push(interpolateTemplate(cartTemplate, {
        __COUNT__: cartQuantity
      }));
    }

    this.volumeIntro.hidden = introParts.length === 0;
    this.volumeIntro.textContent = introParts.join(' ');
  }

  updateHistory(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    window.history.replaceState({}, '', url.toString());
  }

  handleAddToCart(event) {
    event.preventDefault();

    if (this.isSubmitting) {
      return;
    }

    if (!this.canAddToCart(this.getPurchaseQuantities())) {
      return;
    }

    this.isSubmitting = true;
    this.addToCartButton.classList.add('pdp-cfy-actions__add--loading');
    this.addLineItemsToCart();
  }

  async addLineItemsToCart() {
    const cartLineItems = this.buildCartLineItems();

    try {
      const lineItem = await this.requestCartAdd(cartLineItems);

      this.recordAddedCartLineItems(cartLineItems);

      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: {
          product: lineItem,
          addToCartBtn: this.addToCartButton
        }
      }));
    } catch (error) {
      console.error(error);

      // Shopify CLI previews can return a plain-text 401 from /cart/add.js
      // ("The access token provided is expired..."). The HTML product form
      // still uses the storefront session, so fall back instead of alerting JSON parse noise.
      // The form only posts one id/quantity pair, so color selections cannot use this fallback.
      if (error.shouldFallbackToHtmlForm && !this.isColorPurchase) {
        this.form.submit();
        return;
      }

      window.alert(error.message);
    } finally {
      this.addToCartButton.classList.remove('pdp-cfy-actions__add--loading');
      this.isSubmitting = false;
    }
  }

  recordAddedCartLineItems(cartLineItems) {
    // /cart/add.js line.quantity is the merged line total, not units just added.
    cartLineItems.forEach((cartLineItem) => {
      const addedVariant = this.getVariantById(cartLineItem.id);
      addedVariant.cartQuantity = resolveCartQuantity(addedVariant.cartQuantity) + cartLineItem.quantity;
    });

    if (this.isColorPurchase) {
      this.resetColorQuantities();
      this.render();
      return;
    }

    this.setAddQuantity(this.getMinimumAddQuantity(this.state.selectedVariant));
  }

  async requestCartAdd(cartLineItems) {
    const response = await fetch(getCartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: cartLineItems
      })
    });

    const responseText = await response.text();
    const parsedResponse = parseJsonResponse(responseText);

    if (!parsedResponse) {
      const error = new Error(responseText.trim() || 'Unable to add this product to the cart.');
      error.shouldFallbackToHtmlForm = true;
      throw error;
    }

    if (parsedResponse.status === 422) {
      throw new Error(parsedResponse.description || parsedResponse.message || 'Unable to add this product to the cart.');
    }

    if (parsedResponse.items && parsedResponse.items.length) {
      return parsedResponse.items[0];
    }

    return parsedResponse;
  }
}

function initializeCfyProductPages() {
  const productRoots = document.querySelectorAll('.js-pdp-cfy');
  productRoots.forEach((rootElement) => {
    new ProductPurchaseController(rootElement);
  });
}

function initializeCfyProductFromSectionEvent(event) {
  const rootElement = event.target.querySelector('.js-pdp-cfy');
  if (!rootElement) {
    return;
  }

  new ProductPurchaseController(rootElement);
}

document.addEventListener('DOMContentLoaded', initializeCfyProductPages);
document.addEventListener('shopify:section:load', initializeCfyProductFromSectionEvent);
